import React, { useState, useEffect } from "react";
import { FaBookOpen } from "react-icons/fa";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { hasPermission } from "../../utils/authUtils";
import { apiFetch } from "../../utils/api";
import { showError } from "../../utils/toast";


const decodeJWT = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

export default function CreateTask() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editingBook = location.state?.book || null;
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [isbn, setIsbn] = useState("");
  const [genre, setGenre] = useState("");
  const [publisher, setPublisher] = useState("");
  const [yearOfPublication, setYearOfPublication] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;
  const [userId, setUserId] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);

  const canCreateBook = hasPermission('book:create');
  const canEditBooks = hasPermission('book:update') || hasPermission('book:update:all');

  useEffect(() => {
    // Guard: require token to access this page
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/unauthorized?from=' + encodeURIComponent(window.location.pathname + window.location.search));
      return;
    }

    // Check for token in URL parameters
    const tokenFromUrl = searchParams.get('token');
    
    if (tokenFromUrl) {
      // Decode the JWT token
      const decodedToken = decodeJWT(tokenFromUrl);
      
      if (decodedToken) {
        // Extract user ID and permissions from the token
        setUserId(decodedToken.user_id || decodedToken.sub || decodedToken.uid);
        
        // Extract permissions 
        const permissions = decodedToken.permissions || 
                           decodedToken.scope?.split(' ') || 
                           decodedToken.roles || 
                           [];
        setUserPermissions(Array.isArray(permissions) ? permissions : [permissions]);
        
        // Store the token in localStorage for future use
        try {
          localStorage.setItem('token', tokenFromUrl);
          localStorage.setItem('userPermissions', JSON.stringify(permissions));
          localStorage.setItem('userId', decodedToken.user_id || decodedToken.sub || decodedToken.uid);
        } catch (error) {
          console.error('Error storing token in localStorage:', error);
        }
        
        // Remove token from URL for security
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    } else {
      // Try to get permissions from localStorage if no token in URL
      try {
        const storedPermissions = localStorage.getItem('userPermissions');
        const storedUserId = localStorage.getItem('userId');
        
        if (storedPermissions) {
          setUserPermissions(JSON.parse(storedPermissions));
        }
        if (storedUserId) {
          setUserId(storedUserId);
        }
      } catch (error) {
        console.error('Error reading from localStorage:', error);
      }
    }
  }, [searchParams]);

  //  const canCreateBook = hasPermission('book:create', userPermissions);
  // const canEditBooks = hasPermission('book:update', userPermissions) || 
  //                     hasPermission('book:update:all', userPermissions);

   useEffect(() => {
    if ((editingBook && !canEditBooks) || (!editingBook && !canCreateBook)) {
      showError("You don't have permission to perform this action");
      navigate("/book-list");
    }
  }, [editingBook, canCreateBook, canEditBooks, navigate]);


  useEffect(() => {
    if (editingBook && canEditBooks) {
      setTitle(editingBook.title);
      setAuthor(editingBook.author);
      setIsbn(editingBook.isbn);
      setGenre(editingBook.genre);
      setPublisher(editingBook.publisher);
      setYearOfPublication(editingBook.year_of_publication || "");
    }
  }, [editingBook, canEditBooks]);


const handleSubmit = async (e) => {
  setIsLoading(true);
  e.preventDefault();

  const newBook = {
    title,
    author,
    isbn,
    genre,
    publisher,
    year_of_publication: yearOfPublication
      ? parseInt(yearOfPublication)
      : null,
  };

  if (!newBook.title.trim()) {
    setIsLoading(false);
    return showError("Title cannot be empty");
  }
  if (!newBook.author.trim()) {
    setIsLoading(false);
    return showError("Author cannot be empty");
  }
  if (!newBook.isbn.trim()) {
    setIsLoading(false);
    return showError("ISBN cannot be empty");
  }

  try {
    const url = editingBook
      ? `${API_URL}/books/${editingBook.id}` 
      : `${API_URL}/books`; 

    const method = editingBook ? "PUT" : "POST";

    const response = await apiFetch(url, {
      method,
      body: JSON.stringify(newBook),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData?.message || errorData?.error || `Failed to ${editingBook ? 'update' : 'create'} book`);
    }

    const data = await response.json();

    // Reset
    setTitle("");
    setAuthor("");
    setIsbn("");
    setGenre("");
    setPublisher("");
    setYearOfPublication("");

    // Navigate back to list
    navigate(-1);
  } catch (error) {
    console.error("❌ Error:", error);
    showError(error.message || 'An unexpected error occurred');
  } finally {
    setIsLoading(false);
  }
};


  return (
    <div style={{ paddingLeft: "380px", width: "500px" }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: "20px", marginTop: "20px", marginLeft: "80px" }}>
        <FaBookOpen style={{ fontSize: "60px", color: "#00008B", marginRight: "15px" }} />
        <span style={{ fontSize: "50px", fontWeight: 200}}>
          Book<span style={{ color: "#86c5da" }}>List</span> App
        </span>
      </div>


      <form onSubmit={handleSubmit}>
        <div style={{ display: "flex", flexDirection: "column", marginBottom: "15px" }}>
          <label
            htmlFor="title"
            style={{ marginBottom: "5px", fontWeight: 500, alignSelf: "flex-start", color: "#808080" }}
          >
            Title
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter book title"
            style={{ padding: "8px", borderRadius: "4px", fontWeight: 500, border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", marginBottom: "15px" }}>
          <label style={{ marginBottom: "5px", fontWeight: 500, alignSelf: "flex-start", color: "#808080" }}>
            Author
          </label>
          <input
            type="text"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            placeholder="Book author"
            style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", marginBottom: "15px" }}>
          <label style={{ marginBottom: "5px", fontWeight: 500, alignSelf: "flex-start", color: "#808080" }}>
            ISBN#
          </label>
          <input
            type="text"
            value={isbn}
            onChange={(e) => setIsbn(e.target.value)}
            placeholder="Enter ISBN number"
            style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", marginBottom: "15px" }}>
          <label style={{ marginBottom: "5px", fontWeight: 500, alignSelf: "flex-start", color: "#808080" }}>
            Genre
          </label>
          <input
            type="text"
            value={genre}
            onChange={(e) => setGenre(e.target.value)}
            placeholder="Enter book genre"
            style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", marginBottom: "15px" }}>
          <label style={{ marginBottom: "5px", fontWeight: 500, alignSelf: "flex-start", color: "#808080" }}>
            Publisher
          </label>
          <input
            type="text"
            value={publisher}
            onChange={(e) => setPublisher(e.target.value)}
            placeholder="Enter publisher name"
            style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", marginBottom: "15px" }}>
          <label style={{ marginBottom: "5px", fontWeight: 500, alignSelf: "flex-start", color: "#808080" }}>
            Year of Publication
          </label>
          <input
            type="number"
            value={yearOfPublication}
            onChange={(e) => setYearOfPublication(e.target.value)}
            placeholder="Enter year of publication"
            style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{marginLeft: "140px", marginTop: "50px"}}>
        <button type="submit" 
                style={{
                  backgroundColor: isLoading ? "#ccc" : "#00008B",
                  cursor: isLoading ? "not-allowed" : "pointer",
                  color: "white",
                  padding: "10px 26px",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
                >

          {isLoading ? "Submitting..." : (editingBook ? "Update" : "SUBMIT")}
        </button>
        <button
          style={{
            backgroundColor: "#6a6a6a",
            color: "white",
            padding: "10px 26px",
            border: "none",
            borderRadius: "4px",
            marginLeft: "10px",
          }}
          type="button"
          onClick={() => {
            if (editingBook) {
              // If editing → go back to book list
              navigate("/book-list");
            } else {
              // If creating → just clear the form
              setTitle("");
              setAuthor("");
              setIsbn("");
              setGenre("");
              setPublisher("");
              setYearOfPublication("");
            }
          }}
        >
          CANCEL
        </button>

        </div>
      </form>
    </div>
  );
}
